import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import ReferenceSelectionModal from '@UI/modals/ReferenceSelectionModal';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import { selectReferenceNodes } from '@newStore/documentUI/transformContent/referenceGroupSelectors';
import type { EditAsideReferenceGroup } from '@nodeTypeConfig/configTypes';
import {
  addReferenceFromNodeAction,
  removeNodeAction,
  removeRelationAction,
} from '@store/actions/documentActions';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { any } from 'prop-types';
import type React from 'react';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { ReferenceGroupReference } from '../../../../store/documentUI/documentUITypes';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useTypedSelector } from '../../../hooks/useTypedSelector';

const AsideReferenceGroup: React.FC<{
  config: EditAsideReferenceGroup;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const dispatch = useDispatch();
  const editHref = useEditHref();
  const editKey = getResourceKey(editHref);
  const documentModalRef = useRef<Modal>();
  const confirmationService = getAngularService('ConfirmationService');

  const [selectedReferences, setSelectedReferences] = useState(new Set());

  const changeSelectedReference = (checked, referenceHref) => {
    const newSelectedReferences = new Set(selectedReferences);
    if (checked) {
      newSelectedReferences.add(referenceHref);
    } else {
      newSelectedReferences.delete(referenceHref);
    }
    setSelectedReferences(newSelectedReferences);
  };

  const removeReferences = async (references: ReferenceGroupReference[]) => {
    if (references.length === 0) {
      return;
    }
    const confirmed = await confirmationService.confirmDeletion();
    if (!confirmed) {
      return;
    }
    for (const reference of references) {
      const refsInNode = references.filter(
        (r) => r.referenceNodeHref === reference.referenceNodeHref,
      );
      // as far as I know, each reference has its own REFERENCE content node, so it will always be the removeNodeAction.
      if (refsInNode.length === 1) {
        // remove REFERENCE node since this is the last REFERENCES relation
        dispatch(removeNodeAction(getResourceKey(reference.referenceNodeHref), true, editKey));
      } else {
        // only remove REFERENCES relation
        dispatch(removeRelationAction(getResourceKey(reference.referenceRelationHref)));
      }
    }
  };

  const addReference = async () => {
    documentModalRef.current?.open({
      onSubmit: (response) => {
        if (response) {
          dispatch(
            addReferenceFromNodeAction(editKey, response.resourceHref, response.description),
          );
        }
      },
    });
  };

  const references = useTypedSelector((state) => selectReferenceNodes(state, editHref));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <label className="control-label control-label-inline" style={{ width: '50%' }}>
          {config.label}
        </label>
        <div>
          {!readonly && (
            <button
              className="btn btn-default"
              style={{ marginRight: '10px' }}
              onClick={() =>
                removeReferences(
                  references.filter((r) => selectedReferences.has(r.referenceRelationHref)),
                )
              }
            >
              <span className="glyphicon glyphicon-delete" />
              <span>Verwijderen</span>
            </button>
          )}
          {!readonly && (
            <button className="btn btn-default" onClick={addReference}>
              <span className="glyphicon glyphicon-add" />
              <span>Voeg toe</span>
            </button>
          )}
        </div>
      </div>
      {references.length > 0 && (
        <div className="structure">
          {references.map((relation) => (
            <div className="row table-row" key={relation.referenceRelationHref}>
              <div className="col-md-1">
                <input
                  type="checkbox"
                  checked={selectedReferences.has(relation.referenceRelationHref)}
                  onChange={(input) =>
                    changeSelectedReference(input.target.checked, relation.referenceRelationHref)
                  }
                />
              </div>
              <div className="col-md-1 picker-result">
                <img
                  src={relation.icon}
                  alt="icon"
                  className="thumb"
                  style={{ marginTop: '-2px' }}
                />
              </div>
              <div className="col-md-9">
                <span dangerouslySetInnerHTML={{ __html: relation?.title }} />
              </div>
              {!readonly && (
                <div className="btn-group btn-group-xs row-actions" role="group" aria-label="...">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => removeReferences([relation])}
                  >
                    <span className="glyphicon glyphicon-delete" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Modal
        ref={documentModalRef}
        size="sm"
        component={
          <ReferenceSelectionModal selectedResourceHrefs={references.map((z) => z.href)} />
        }
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

AsideReferenceGroup.displayName = 'AsideReferenceGroup';

AsideReferenceGroup.propTypes = {
  config: any,
  readonly: any,
};

export default AsideReferenceGroup;
